import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Document, isMap, isNode, isScalar, isSeq, parseDocument } from 'yaml'
import styles from './ConfigEditor.module.css'

interface Props {
  initialValue: Record<string, unknown> | null
  revision: number
  readOnly?: boolean
  onSave: (parsed: Record<string, unknown>) => Promise<{ revision: number }>
  saving?: boolean
  /** 'global' | 'site' | 'node' — drives template options */
  configScope?: 'global' | 'site' | 'node'
}

interface TemplateOption {
  label: string
  description?: string
  group?: string
  apply: (current: Record<string, unknown>) => Record<string, unknown>
}

const LOCAL_RECORD_DEFAULTS = {
  rootDir: '/data/manual',
  freeDiskThresholdPercent: 5,
  scanMinFileAgeSeconds: 30,
  scanStableRequiredCount: 2,
}

const CANONICAL_KEY_ORDER: Record<string, readonly string[]> = {
  '': [
    'schema_version',
    'heartbeat_interval_sec',
    'command_poll_interval_sec',
    'cameras',
    'analysis',
    'recording',
    'upload',
  ],
  'cameras[]': ['key', 'name', 'source'],
  analysis: [
    'service_name',
    'analysis_node_name',
    'root_dir',
    'config_path',
    'weights_dir',
    'model_slots_dir',
    'working_dir',
    'model_staging_dir',
    'gf_token_ref',
    'guardian_nodes',
    'restart_command',
    'health_check_command',
    'cameras',
  ],
  'analysis.cameras[]': ['camera_key', 'name', 'external_aliases', 'config_target'],
  'analysis.cameras[].config_target': ['mode', 'weight_index', 'slot_format'],
  'analysis.guardian_nodes[]': ['analysis_node_name', 'config_path', 'working_dir', 'cameras'],
  'analysis.guardian_nodes[].cameras[]': ['camera_key', 'name', 'external_aliases', 'config_target'],
  'analysis.guardian_nodes[].cameras[].config_target': ['mode', 'weight_index', 'slot_format'],
  recording: ['disk_guard', 'capture_defaults', 'scan', 'manual', 'inference'],
  'recording.disk_guard': ['min_free_percent'],
  'recording.capture_defaults': [
    'width',
    'height',
    'fps',
    'chunk_seconds',
    'motion_threshold',
    'recording_times',
    'rotate',
    'flip_vertical',
    'flip_horizontal',
  ],
  'recording.scan': [
    'extensions',
    'recursive',
    'min_file_age_seconds',
    'stable_required_count',
    'report_interval_seconds',
    'probe_metadata',
  ],
  'recording.manual': ['enabled', 'base_dir'],
  'recording.inference': ['enabled', 'base_dir'],
  upload: [
    'protocol',
    'host',
    'port',
    'user',
    'password',
    'base_dir',
    'base_fallback_dirs',
    'chunk_size',
    'max_retry',
    'bandwidth_limit_mbps',
    'naming',
    'auto',
  ],
  'upload.naming': ['enabled', 'prefix'],
  'upload.auto': ['manual', 'inference'],
  'upload.auto.manual': ['enabled', 'schedule', 'file_window', 'delete_local_after_success'],
  'upload.auto.inference': ['enabled', 'schedule', 'file_window', 'delete_local_after_success'],
  'upload.auto.manual.schedule': ['local_time', 'timezone'],
  'upload.auto.inference.schedule': ['local_time', 'timezone'],
}

const KEY_COLLATOR = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function deepMerge(base: Record<string, unknown>, patch: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = { ...base }
  for (const [k, v] of Object.entries(patch)) {
    if (
      v !== null &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      typeof result[k] === 'object' &&
      result[k] !== null &&
      !Array.isArray(result[k])
    ) {
      result[k] = deepMerge(result[k] as Record<string, unknown>, v as Record<string, unknown>)
    } else {
      result[k] = v
    }
  }
  return result
}

function cameraKeysFromConfig(config: Record<string, unknown>): string[] {
  const canonical = Array.isArray(config.cameras) ? config.cameras : []
  const recording = asRecord(config.recording)
  const cameras = canonical.length > 0
    ? canonical
    : Array.isArray(recording.cameras) ? recording.cameras : []
  const keys = cameras
    .map((cam) => asRecord(cam).key)
    .filter((key): key is string => typeof key === 'string' && key.trim().length > 0)
  return keys.length > 0 ? keys : ['cam0', 'cam1']
}

function remotePathsForCameraKeys(keys: string[]): Record<string, string> {
  return Object.fromEntries(keys.map((key, idx) => [key, `/VOL1/anno/C00004/BCT/line1_cam${idx}`]))
}

function cameraIndexFromKey(key: string, fallback: number): number {
  const match = key.match(/(\d+)$/)
  return match ? Number(match[1]) : fallback
}

function nextCameraKey(existingKeys: string[]): string {
  const used = new Set(existingKeys)
  let idx = 0
  while (used.has(`cam${idx}`)) idx += 1
  return `cam${idx}`
}

function defaultRemotePath(key: string, fallbackIdx: number): string {
  return `/VOL1/anno/C00004/BCT/line1_cam${cameraIndexFromKey(key, fallbackIdx)}`
}

function defaultManagedRecordingCamera(
  key: string,
  idx: number,
  current?: Record<string, unknown>,
): Record<string, unknown> {
  return {
    key,
    name: typeof current?.name === 'string' ? current.name : `Camera ${cameraIndexFromKey(key, idx)}`,
    source: typeof current?.source === 'string' ? current.source : `secret:rtsp-${key}:node:node-id`,
  }
}

function managedRecordingCamerasForCameraKeys(
  keys: string[],
  currentCameras: unknown[] = [],
): Array<Record<string, unknown>> {
  const currentByKey = new Map(
    currentCameras
      .map((cam) => asRecord(cam))
      .filter((cam) => typeof cam.key === 'string')
      .map((cam) => [cam.key as string, cam]),
  )
  return keys.map((key, idx) => {
    const current = currentByKey.get(key)
    return defaultManagedRecordingCamera(key, idx, current)
  })
}

function applyRemotePathsTemplate(cur: Record<string, unknown>): Record<string, unknown> {
  const upload = asRecord(cur.upload)
  const currentRemotePaths = asRecord(upload.base_fallback_dirs)
  return {
    ...cur,
    schema_version: 2,
    upload: {
      ...upload,
      base_fallback_dirs: {
        ...remotePathsForCameraKeys(cameraKeysFromConfig(cur)),
        ...currentRemotePaths,
      },
    },
  }
}

export function applyManagedRecordingTemplate(cur: Record<string, unknown>): Record<string, unknown> {
  const recording = asRecord(cur.recording)
  const cameras = Array.isArray(cur.cameras) ? cur.cameras : []
  const keys = cameraKeysFromConfig(cur)
  return {
    ...cur,
    schema_version: 2,
    cameras: managedRecordingCamerasForCameraKeys(keys, cameras),
    recording: {
      ...recording,
      disk_guard: {
        min_free_percent: LOCAL_RECORD_DEFAULTS.freeDiskThresholdPercent,
      },
      // 신규 템플릿은 크기를 강제하지 않고, 아래 spread로 사용자가 명시한 기존 크기는 보존한다.
      capture_defaults: {
        fps: 5,
        chunk_seconds: 1800,
        motion_threshold: 0,
        recording_times: null,
        rotate: null,
        flip_vertical: false,
        flip_horizontal: false,
        ...asRecord(recording.capture_defaults),
      },
      scan: {
        extensions: ['.mp4'],
        recursive: false,
        min_file_age_seconds: LOCAL_RECORD_DEFAULTS.scanMinFileAgeSeconds,
        stable_required_count: LOCAL_RECORD_DEFAULTS.scanStableRequiredCount,
        report_interval_seconds: 60,
        probe_metadata: true,
        ...asRecord(recording.scan),
      },
      manual: {
        enabled: true,
        base_dir: LOCAL_RECORD_DEFAULTS.rootDir,
        ...asRecord(recording.manual),
      },
    },
  }
}

function applyNodeEssentialTemplate(cur: Record<string, unknown>): Record<string, unknown> {
  const recordingConfig = applyManagedRecordingTemplate(cur)
  const keys = cameraKeysFromConfig(recordingConfig)
  const upload = asRecord(recordingConfig.upload)
  return {
    ...recordingConfig,
    upload: {
      ...upload,
      base_dir: typeof upload.base_dir === 'string' ? upload.base_dir : '/data/inbox',
      base_fallback_dirs: {
        ...remotePathsForCameraKeys(keys),
        ...asRecord(upload.base_fallback_dirs),
      },
      naming: {
        enabled: true,
        prefix: 'company__site__line',
        ...asRecord(upload.naming),
      },
      auto: {
        manual: {
          enabled: false,
          schedule: { local_time: '02:00', timezone: 'Asia/Seoul' },
          file_window: 'previous_day',
          delete_local_after_success: false,
        },
        inference: {
          enabled: false,
          schedule: { local_time: '02:00', timezone: 'Asia/Seoul' },
          file_window: 'previous_day',
          delete_local_after_success: true,
        },
        ...asRecord(upload.auto),
      },
    },
  }
}

function applyAddCameraTemplate(cur: Record<string, unknown>): Record<string, unknown> {
  const cameras = Array.isArray(cur.cameras) ? [...cur.cameras] : []
  const existingKeys = cameras
    .map((cam) => asRecord(cam).key)
    .filter((key): key is string => typeof key === 'string' && key.trim().length > 0)
  const key = nextCameraKey(existingKeys)
  const idx = cameras.length
  cameras.push(defaultManagedRecordingCamera(key, idx))

  const upload = asRecord(cur.upload)
  const remotePaths = asRecord(upload.base_fallback_dirs)

  return {
    ...cur,
    schema_version: 2,
    cameras,
    upload: {
      ...upload,
      base_fallback_dirs: {
        ...remotePaths,
        [key]: defaultRemotePath(key, idx),
      },
    },
  }
}

function applyInferenceRecordingTemplate(cur: Record<string, unknown>): Record<string, unknown> {
  const recording = asRecord(cur.recording)

  return {
    ...cur,
    schema_version: 2,
    recording: {
      ...recording,
      inference: {
        enabled: true,
        base_dir: '/data/infer',
        ...asRecord(recording.inference),
      },
    },
  }
}

function analysisCamerasForCameraKeys(
  keys: string[],
  currentAnalysisCameras: unknown[] = [],
  currentRecordingCameras: unknown[] = [],
): Array<Record<string, unknown>> {
  const analysisByKey = new Map(
    currentAnalysisCameras
      .map((cam) => asRecord(cam))
      .filter((cam) => typeof cam.camera_key === 'string')
      .map((cam) => [cam.camera_key as string, cam]),
  )
  const recordingByKey = new Map(
    currentRecordingCameras
      .map((cam) => asRecord(cam))
      .filter((cam) => typeof cam.key === 'string')
      .map((cam) => [cam.key as string, cam]),
  )

  return keys.map((key, idx) => {
    const current = analysisByKey.get(key)
    const recording = recordingByKey.get(key)
    const currentTarget = asRecord(current?.config_target)
    const configTarget = currentTarget.mode
      ? {
          ...currentTarget,
          slot_format: typeof currentTarget.slot_format === 'string' ? currentTarget.slot_format : 'openvino',
        }
      : {
          mode: 'top_level_weight_index',
          weight_index: idx,
          slot_format: 'openvino',
        }
    return {
      camera_key: key,
      name: typeof current?.name === 'string'
        ? current.name
        : typeof recording?.name === 'string'
          ? recording.name
          : `Camera ${cameraIndexFromKey(key, idx)}`,
      external_aliases: Array.isArray(current?.external_aliases) ? current.external_aliases : [],
      config_target: configTarget,
    }
  })
}

function applyModelDeploymentTemplate(cur: Record<string, unknown>): Record<string, unknown> {
  const analysis = asRecord(cur.analysis)
  const keys = cameraKeysFromConfig(cur)
  const currentAnalysisCameras = Array.isArray(analysis.cameras) ? analysis.cameras : []
  const currentRecordingCameras = Array.isArray(cur.cameras) ? cur.cameras : []

  return {
    ...cur,
    analysis: {
      ...analysis,
      service_name: typeof analysis.service_name === 'string' ? analysis.service_name : 'guardian-alpha',
      analysis_node_name: typeof analysis.analysis_node_name === 'string' ? analysis.analysis_node_name : 'node-name',
      root_dir: typeof analysis.root_dir === 'string' ? analysis.root_dir : '/home/node/node-name',
      working_dir: typeof analysis.working_dir === 'string' ? analysis.working_dir : '/home/node/node-name',
      config_path: typeof analysis.config_path === 'string'
        ? analysis.config_path
        : '/home/node/node-name/node/node-name/config.yml',
      weights_dir: typeof analysis.weights_dir === 'string'
        ? analysis.weights_dir
        : '/var/lib/node-agent/analysis/weights',
      model_slots_dir: typeof analysis.model_slots_dir === 'string'
        ? analysis.model_slots_dir
        : '/var/lib/node-agent/analysis/model-slots',
      gf_token_ref: typeof analysis.gf_token_ref === 'string'
        ? analysis.gf_token_ref
        : 'secret:gf-deploy-token:node:node-id',
      cameras: analysisCamerasForCameraKeys(keys, currentAnalysisCameras, currentRecordingCameras),
    },
  }
}

function getTemplates(scope: 'global' | 'site' | 'node'): TemplateOption[] {
  if (scope === 'global') {
    return [
      {
        label: '기본 스켈레톤',
        description: '전역 FTP 접속/업로드 기본값',
        apply: () => ({
          schema_version: 2,
          heartbeat_interval_sec: 15,
          command_poll_interval_sec: 5,
          upload: {
            protocol: 'ftp',
            host: 'ftp.example.com',
            port: 21,
            user: 'cctv',
            password: 'secret:ftp-password:global:global',
            chunk_size: 104857600,
            max_retry: 5,
            bandwidth_limit_mbps: 20,
          },
        }),
      },
      {
        label: '+ Upload 설정 추가',
        description: '현재 YAML에 공통 upload 설정만 병합',
        apply: (cur) => deepMerge(cur, { schema_version: 2, upload: { protocol: 'ftp', host: '', port: 21, user: '', password: 'secret:ftp-password:global:global', chunk_size: 104857600, max_retry: 5 } }),
      },
    ]
  }

  if (scope === 'site') {
    return [
      {
        label: '기본 스켈레톤',
        description: 'site 단위 remote_base 예시',
        apply: () => ({
          upload: { remote_base: '/VOL1/anno/site-name' },
        }),
      },
      {
        label: '+ Upload remote_base 추가',
        description: 'site 단위 legacy fallback 경로',
        apply: (cur) => deepMerge(cur, { upload: { remote_base: '/VOL1/anno/site-name' } }),
      },
      {
        label: '+ RTSP credential 추가',
        description: 'site 단위 RTSP 시크릿 reference',
        apply: (cur) => deepMerge(cur, { rtsp: { user: 'admin', password: 'secret:rtsp-password:site:site-name' } }),
      },
      {
        label: '+ Storage 경로 추가',
        description: 'site 단위 local_root 예시',
        apply: (cur) => deepMerge(cur, { storage: { local_root: '/mnt/nas/site-name' } }),
      },
    ]
  }

  // node
  return [
    {
      label: '필수 Node Override 구조',
      description: 'v2 camera, recording, upload canonical 구조',
      group: '필수',
      apply: applyNodeEssentialTemplate,
    },
    {
      label: '+ Manual managed recording 추가',
      description: 'Node Override 기반 수동 녹화 설정 + 파일 스캔 설정',
      group: '필수',
      apply: applyManagedRecordingTemplate,
    },
    {
      label: '+ 카메라 1대 추가',
      description: 'cameras와 upload.base_fallback_dirs에 같은 key 추가',
      group: '카메라',
      apply: applyAddCameraTemplate,
    },
    {
      label: '+ 카메라별 NAS 업로드 경로',
      description: 'cameras[].key 기준으로 base_fallback_dirs 생성',
      group: '카메라',
      apply: applyRemotePathsTemplate,
    },
    {
      label: '+ 추론 녹화 설정 추가',
      description: 'recording.inference 파일 발견 경로 추가',
      group: '선택',
      apply: applyInferenceRecordingTemplate,
    },
    {
      label: '+ 에이전트 배포 템플릿',
      description: 'model.deploy용 analysis 경로/GF token/config_target 기본 구조',
      group: '선택',
      apply: applyModelDeploymentTemplate,
    },
    {
      label: '+ Retention 예외 추가',
      description: '노드별 보관 기간 override',
      group: '선택',
      apply: (cur) => deepMerge(cur, { retention: { mp4_days: 14, event_days: 60 } }),
    },
    {
      label: '+ RTSP 시크릿 카메라',
      description: 'managed recording에서 쓸 source 예시',
      group: '선택',
      apply: (cur) => {
        const cameras = Array.isArray(cur.cameras) ? [...cur.cameras] : []
        const key = nextCameraKey(
          cameras
            .map((cam) => asRecord(cam).key)
            .filter((camKey): camKey is string => typeof camKey === 'string'),
        )
        cameras.push(defaultManagedRecordingCamera(key, cameras.length, { name: 'Secret Camera' }))
        return { ...cur, schema_version: 2, cameras }
      },
    },
  ]
}

function transformEditorSelection(
  value: string,
  start: number,
  end: number,
  outdent: boolean,
): { next: string; start: number; end: number } {
  const indent = '  '
  if (start === end && !outdent) {
    const cursor = start + indent.length
    return {
      next: value.slice(0, start) + indent + value.slice(end),
      start: cursor,
      end: cursor,
    }
  }

  const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1
  const lineEnd = start === end && outdent
    ? (() => {
        const nextNewline = value.indexOf('\n', start)
        return nextNewline === -1 ? value.length : nextNewline
      })()
    : end
  const block = value.slice(lineStart, lineEnd)
  const beforeStart = value.slice(lineStart, start)
  const beforeEnd = value.slice(lineStart, end)

  const transform = (input: string) => input
    .split('\n')
    .map((line) => {
      if (!outdent) return indent + line
      if (line.startsWith(indent)) return line.slice(indent.length)
      if (line.startsWith('\t')) return line.slice(1)
      if (line.startsWith(' ')) return line.slice(1)
      return line
    })
    .join('\n')

  const transformedBlock = transform(block)
  const transformedBeforeStart = transform(beforeStart)
  const transformedBeforeEnd = transform(beforeEnd)

  return {
    next: value.slice(0, lineStart) + transformedBlock + value.slice(lineEnd),
    start: lineStart + transformedBeforeStart.length,
    end: lineStart + transformedBeforeEnd.length,
  }
}

export function formatManagedConfigYaml(value: Record<string, unknown>): string {
  const document = new Document(value)

  function formatNode(node: unknown, path = '', depth = 0): void {
    if (isMap(node)) {
      const order = CANONICAL_KEY_ORDER[path] ?? []
      node.items.sort((left, right) => {
        const leftKey = isScalar(left.key) ? String(left.key.value) : ''
        const rightKey = isScalar(right.key) ? String(right.key.value) : ''
        const leftRank = order.indexOf(leftKey)
        const rightRank = order.indexOf(rightKey)
        if (leftRank >= 0 && rightRank >= 0) return leftRank - rightRank
        if (leftRank >= 0) return -1
        if (rightRank >= 0) return 1
        return KEY_COLLATOR.compare(leftKey, rightKey)
      })

      node.items.forEach((pair, index) => {
        if (index > 0 && isNode(pair.key) && (depth === 0 || isMap(pair.value))) {
          pair.key.spaceBefore = true
        }
        if (isNode(pair.value)) {
          const key = isScalar(pair.key) ? String(pair.key.value) : ''
          const childPath = path ? `${path}.${key}` : key
          formatNode(pair.value, childPath, depth + 1)
        }
      })
      return
    }

    if (isSeq(node)) {
      node.items.forEach((item) => {
        if (isNode(item)) formatNode(item, `${path}[]`, depth + 1)
      })
    }
  }

  formatNode(document.contents)
  return document.toString({ indent: 2, lineWidth: 0 })
}

function parseYamlWithPosition(raw: string): { value: Record<string, unknown> } | { error: string; line?: number; col?: number } {
  try {
    const document = parseDocument(raw, { prettyErrors: false })
    const firstError = document.errors[0]
    if (firstError) {
      const position = (firstError as { linePos?: Array<{ line: number; col: number }> }).linePos?.[0]
      return { error: firstError.message, line: position?.line, col: position?.col }
    }
    const value: unknown = document.toJS()
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return { error: 'Config root must be a YAML mapping' }
    }
    return { value: value as Record<string, unknown> }
  } catch (e: unknown) {
    return { error: (e as { message?: string })?.message ?? 'Invalid YAML' }
  }
}

function configDiff(a: Record<string, unknown>, b: Record<string, unknown>): { added: string[]; removed: string[]; changed: string[] } {
  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)])
  const added: string[] = []
  const removed: string[] = []
  const changed: string[] = []
  allKeys.forEach((k) => {
    if (!(k in a)) added.push(k)
    else if (!(k in b)) removed.push(k)
    else if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) changed.push(k)
  })
  return { added, removed, changed }
}

export function formatConfigSaveError(error: unknown): string {
  // API client가 문자열로 전달한 검증 JSON을 편집기에서 읽을 수 있는 안내로 바꾼다.
  const message = (error as { message?: string } | null)?.message
  if (!message) return '설정을 저장하지 못했습니다.'
  try {
    const detail = JSON.parse(message)
    if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
      const reasons = detail.errors.flatMap((item: unknown) => {
        if (!item || typeof item !== 'object') return []
        const { loc, msg } = item as { loc?: unknown; msg?: unknown }
        if (typeof msg !== 'string') return []
        // Pydantic의 공통 접두사는 숨기고 수정할 YAML 경로와 원인 메시지를 함께 보여준다.
        const reason = msg.replace(/^Value error,\s*/, '')
        return [typeof loc === 'string' ? `${loc}: ${reason}` : reason]
      })
      return ['설정을 저장하지 못했습니다.', ...reasons].join('\n')
    }
  } catch {
    // Plain-text API/network errors keep their original message.
    // 네트워크 오류처럼 JSON이 아닌 메시지는 원문을 유지해 실제 원인을 잃지 않게 한다.
  }
  return message
}

export default function ConfigEditor({ initialValue, revision, readOnly, onSave, configScope = 'global' }: Props) {
  const originalValue = initialValue ?? {}
  const originalStr = formatManagedConfigYaml(originalValue)
  const [text, setText] = useState(originalStr)
  const [saveResult, setSaveResult] = useState<{ revision: number } | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [templateOpen, setTemplateOpen] = useState(false)
  const templateBtnRef = useRef<HTMLDivElement>(null)

  const parseResult = parseYamlWithPosition(text)
  const parseError = 'error' in parseResult ? parseResult : null
  const parsedValue = 'value' in parseResult ? parseResult.value : null

  // Reset when the initialValue changes (tab switch, node/site switch)
  useEffect(() => {
    const fresh = formatManagedConfigYaml(initialValue ?? {})
    setText(fresh)
    setSaveResult(null)
    setSaveError(null)
    setShowConfirm(false)
    setTemplateOpen(false)
  }, [initialValue])

  // Close template dropdown on outside click
  useEffect(() => {
    if (!templateOpen) return
    function handle(e: MouseEvent) {
      if (templateBtnRef.current && !templateBtnRef.current.contains(e.target as Node)) {
        setTemplateOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [templateOpen])

  function handleTextChange(val: string) {
    setText(val)
    setSaveResult(null)
    setSaveError(null)
  }

  function handleEditorKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Tab' || readOnly) return
    e.preventDefault()

    const target = e.currentTarget
    const transformed = transformEditorSelection(
      text,
      target.selectionStart,
      target.selectionEnd,
      e.shiftKey,
    )

    handleTextChange(transformed.next)
    requestAnimationFrame(() => {
      target.selectionStart = transformed.start
      target.selectionEnd = transformed.end
    })
  }

  function handleSaveClick() {
    if (!parsedValue) return
    setShowConfirm(true)
  }

  async function handleConfirm() {
    const result = parseYamlWithPosition(text)
    if ('error' in result) return
    setSaving(true)
    setSaveError(null)
    setSaveResult(null)
    try {
      const saved = await onSave(result.value)
      setSaveResult(saved)
      setShowConfirm(false)
    } catch (e: unknown) {
      // 저장 성공 시에만 revision을 갱신하며, 실패한 입력은 사용자가 고칠 수 있도록 남겨 둔다.
      setSaveError(formatConfigSaveError(e))
      setShowConfirm(false)
    } finally {
      setSaving(false)
    }
  }

  function applyTemplate(opt: TemplateOption) {
    setTemplateOpen(false)
    const current = parsedValue ?? {}
    const next = opt.apply(current)
    setText(formatManagedConfigYaml(next))
    setSaveResult(null)
    setSaveError(null)
  }

  const diff = configDiff(originalValue, parsedValue ?? {})
  const hasDiff = diff.added.length > 0 || diff.removed.length > 0 || diff.changed.length > 0
  const isClean = text === originalStr
  const hasSecretRef = text.includes('secret:')
  const templates = getTemplates(configScope)
  const templateGroups = templates.reduce<Array<{ label: string; items: TemplateOption[] }>>((groups, template) => {
    const label = template.group ?? '템플릿'
    const existing = groups.find((group) => group.label === label)
    if (existing) {
      existing.items.push(template)
    } else {
      groups.push({ label, items: [template] })
    }
    return groups
  }, [])

  const validIndicator = parseError
    ? { label: parseError.line ? `YAML 오류 (line ${parseError.line}, col ${parseError.col})` : 'YAML 오류', className: styles.invalidIndicator }
    : { label: 'YAML 유효', className: styles.validIndicator }

  return (
    <div>
      {/* Toolbar row */}
      <div className={styles.toolbar}>
        {/* Valid indicator */}
        <span className={`${styles.validityIndicator} ${validIndicator.className}`}>
          {validIndicator.label}
        </span>

        <span className={styles.revision}>Rev: {revision}</span>

        {readOnly && (
          <span className={styles.readOnlyBadge}>
            Read-only
          </span>
        )}
        {hasDiff && !isClean && !readOnly && (
          <span className={styles.unsavedBadge}>
            미저장 변경
          </span>
        )}
        {saveResult && (
          <span className={styles.savedBadge}>
            저장 완료 — revision {saveResult.revision}
          </span>
        )}

        {/* Insert template dropdown */}
        {!readOnly && (
          <div ref={templateBtnRef} className={styles.templateMenu}>
            <button
              onClick={() => setTemplateOpen((v) => !v)}
              className={styles.templateButton}
            >
              템플릿 삽입 ▾
            </button>
            {templateOpen && (
              <div className={styles.dropdown}>
                {templateGroups.map((group) => (
                  <div key={group.label}>
                    <div className={styles.dropdownGroupLabel}>{group.label}</div>
                    {group.items.map((t) => (
                      <div
                        key={t.label}
                        className={styles.dropdownItem}
                        onClick={() => applyTemplate(t)}
                      >
                        <div className={styles.dropdownTitle}>{t.label}</div>
                        {t.description && (
                          <div className={styles.dropdownDescription}>
                            {t.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {hasSecretRef && (
        <div className={styles.secretWarning}>
          이 config에 <code className={styles.monospace}>secret:...</code> reference가 포함되어 있습니다.
          실제 값은 표시되지 않으며, Secrets 화면에서 관리하세요.
        </div>
      )}

      <textarea
        value={text}
        onChange={(e) => handleTextChange(e.target.value)}
        onKeyDown={handleEditorKeyDown}
        readOnly={readOnly}
        spellCheck={false}
        className={`${styles.editor} ${parseError ? styles.editorInvalid : ''} ${readOnly ? styles.editorReadOnly : ''}`}
      />

      {parseError && (
        <div className={styles.parseError}>
          YAML 오류: {parseError.error}
          {parseError.line && (
            <span className={styles.errorPosition}>
              line {parseError.line}, col {parseError.col}
            </span>
          )}
        </div>
      )}

      {saveError && (
        <div role="alert" className={styles.saveError}>
          {saveError}
        </div>
      )}

      {/* Diff preview */}
      {hasDiff && !isClean && (
        <div className={styles.diffPreview}>
          <div className={styles.diffHeading}>저장 전/후 비교 (최상위 키)</div>
          {diff.added.map((k) => (
            <div key={k} className={styles.diffAdded}>+ {k}</div>
          ))}
          {diff.removed.map((k) => (
            <div key={k} className={styles.diffRemoved}>- {k}</div>
          ))}
          {diff.changed.map((k) => (
            <div key={k} className={styles.diffChanged}>~ {k}</div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div className={styles.actions}>
          <button
            onClick={() => { setText(originalStr); setSaveResult(null); setSaveError(null) }}
            className={styles.secondaryButton}
            disabled={isClean || saving}
          >
            초기화
          </button>
          <button
            onClick={handleSaveClick}
            className={styles.primaryButton}
            disabled={!!parseError || isClean || saving}
          >
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      )}

      {/* Confirm dialog */}
      {showConfirm && (
        <div className={styles.backdrop} onClick={(e) => { if (e.target === e.currentTarget) setShowConfirm(false) }}>
          <div className={styles.confirmBox} role="dialog" aria-modal="true">
            <div className={styles.confirmHeading}>저장 확인</div>
            <p className={styles.confirmDescription}>
              config 변경사항을 저장합니다. Revision이 +1 증가합니다.
            </p>
            {hasDiff && (
              <div className={styles.confirmDiff}>
                {diff.added.map((k) => <div key={k} className={styles.diffAdded}>+ {k}</div>)}
                {diff.removed.map((k) => <div key={k} className={styles.diffRemoved}>- {k}</div>)}
                {diff.changed.map((k) => <div key={k} className={styles.diffChanged}>~ {k}</div>)}
              </div>
            )}
            <div className={styles.confirmActions}>
              <button onClick={() => setShowConfirm(false)} className={styles.secondaryButton} disabled={saving}>
                취소
              </button>
              <button onClick={handleConfirm} className={styles.primaryButton} disabled={saving}>
                {saving ? '저장 중...' : '확인 저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
